import { useEffect, useState } from 'react'
import {
  CloseButton,
  Button,
  Input,
  NativeSelect,
  Textarea,
  SimpleGrid,
  VStack,
  Box,
  Text,
  HStack,
  Switch,
  Badge,
  Separator,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useForm, useWatch } from 'react-hook-form'
import { FiAlertCircle } from 'react-icons/fi'
import { useCreateWorksite, useUpdateWorksite, useBusinesses, useBusinessDetail, useWorksiteSuggest } from '../../hooks/useBusiness'
import type { SaveWorksiteRequest, WorksiteDetail } from '../../types/business'
import { useNavigate } from 'react-router-dom'
import { ResponsiveModal } from '../shared'

interface BusinessFormModalProps {
  isOpen: boolean
  onClose: () => void
  worksite?: WorksiteDetail // if provided, edit mode
  onCreated?: (id: string) => void
  lockedBusinessId?: string // pre-select and lock business dropdown
}

export function BusinessFormModal({ isOpen, onClose, worksite, onCreated, lockedBusinessId }: BusinessFormModalProps) {
  const navigate = useNavigate()
  const isEdit = !!worksite
  const { data: businesses } = useBusinesses()

  const createMutation = useCreateWorksite()
  const updateMutation = useUpdateWorksite(worksite?.id ?? '')
  const { data: businessDetail } = useBusinessDetail(!isEdit ? lockedBusinessId : undefined)
  const [excludedUnions, setExcludedUnions] = useState<Record<string, boolean>>({})
  const [suggestDismissed, setSuggestDismissed] = useState(false)
  const showUnionCoverage = !isEdit && !!lockedBusinessId && (businessDetail?.unionAffiliations.length ?? 0) > 0

  const { register, handleSubmit, reset, control, formState: { isSubmitting } } = useForm<SaveWorksiteRequest>({
    defaultValues: worksite ? {
      label: worksite.label,
      labelSpanish: worksite.labelSpanish ?? '',
      description: worksite.description ?? '',
      descriptionSpanish: worksite.descriptionSpanish ?? '',
      type: worksite.type ?? '',
      businessId: worksite.businessId ?? null,
      streetAddress: worksite.streetAddress ?? '',
      city: worksite.city ?? '',
      state: worksite.state ?? '',
      zip: worksite.zip ?? '',
      county: worksite.county ?? '',
      addressType: worksite.addressType ?? '',
      latitude: worksite.latitude ?? null,
      longitude: worksite.longitude ?? null,
      email: worksite.email ?? '',
      phone: worksite.phone ?? '',
      poc: worksite.poc ?? '',
      pocPhone: worksite.pocPhone ?? '',
      pocEmail: worksite.pocEmail ?? '',
    } : {
      businessId: lockedBusinessId ?? undefined,
    },
  })

  const labelValue = useWatch({ control, name: 'label' }) ?? ''
  const { data: worksiteSuggestions } = useWorksiteSuggest(labelValue, lockedBusinessId, {
    enabled: !isEdit && !suggestDismissed && labelValue.length >= 2,
  })
  const showSuggestions = !isEdit && !suggestDismissed && worksiteSuggestions && worksiteSuggestions.length > 0

  useEffect(() => {
    if (!isOpen) {
      reset()
      setSuggestDismissed(false)
    }
  }, [isOpen, reset])

  const onSubmit = async (data: SaveWorksiteRequest) => {
    try {
      // Clean up empty strings to null
      const cleaned: SaveWorksiteRequest = {
        ...data,
        businessId: data.businessId || null,
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        unionOverrides: showUnionCoverage ? businessDetail!.unionAffiliations.map(ua => ({
          unionId: ua.unionId,
          isExcluded: !!excludedUnions[ua.unionId],
        })) : undefined,
      }

      if (isEdit) {
        await updateMutation.mutateAsync(cleaned)
        toaster.create({ title: 'Worksite updated', type: 'success', duration: 3000 })
        onClose()
      } else {
        const created = await createMutation.mutateAsync(cleaned)
        toaster.create({ title: 'Worksite created', type: 'success', duration: 3000 })
        onClose()
        if (onCreated && created?.id) onCreated(created.id)
      }
    } catch {
      toaster.create({ title: `Failed to ${isEdit ? 'update' : 'create'} worksite`, type: 'error', duration: 3000 })
    }
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="xl">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Dialog.Header>{isEdit ? 'Edit Worksite' : 'Add Worksite'}</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={4}>
                {/* Basic info */}
                <SimpleGrid columns={2} gap={3} w="full">
                  <Field.Root required>
                    <Field.Label fontSize="sm">Worksite Name</Field.Label>
                    <Input size="sm" {...register('label', { required: true })} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Name (Spanish)</Field.Label>
                    <Input size="sm" {...register('labelSpanish')} />
                  </Field.Root>
                </SimpleGrid>

                {showSuggestions && (
                  <Box w="full" bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                    <HStack gap={2} mb={2}>
                      <FiAlertCircle color="var(--chakra-colors-orange-500)" />
                      <Text fontSize="xs" fontWeight="semibold" color="orange.700">
                        Similar worksites already exist
                      </Text>
                    </HStack>
                    <VStack gap={1} align="stretch">
                      {worksiteSuggestions.map(s => (
                        <HStack
                          key={s.id}
                          p={2}
                          bg="white"
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: 'accent.soft' }}
                          onClick={() => {
                            onClose()
                            navigate(`/dashboard/worksites/${s.id}`)
                          }}
                        >
                          <Box flex={1}>
                            <Text fontSize="sm" fontWeight="medium">{s.label}</Text>
                            <HStack gap={2}>
                              {s.type && <Badge colorPalette="purple" fontSize="2xs">{s.type}</Badge>}
                              {s.city && <Text fontSize="xs" color="gray.500">{s.city}{s.state ? `, ${s.state}` : ''}</Text>}
                              {s.businessName && <Text fontSize="xs" color="gray.400">{s.businessName}</Text>}
                              <Text fontSize="xs" color="gray.400">{Math.round(s.similarity * 100)}% match</Text>
                            </HStack>
                          </Box>
                        </HStack>
                      ))}
                    </VStack>
                    <Button
                      size="xs"
                      variant="ghost"
                      mt={2}
                      onClick={() => setSuggestDismissed(true)}
                      color="gray.500"
                    >
                      None of these — create new
                    </Button>
                  </Box>
                )}

                <SimpleGrid columns={2} gap={3} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm">Type</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field placeholder="Select type" {...register('type')}>
                        <option value="Restaurant">Restaurant</option>
                        <option value="Retail">Retail</option>
                        <option value="Office">Office</option>
                        <option value="Factory">Factory</option>
                        <option value="Warehouse">Warehouse</option>
                        <option value="Service">Service</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Education">Education</option>
                        <option value="Government">Government</option>
                        <option value="Other">Other</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root disabled={!!lockedBusinessId}>
                    <Field.Label fontSize="sm">Business</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        placeholder="No business"
                        {...register('businessId')}>
                        {businesses?.map(b => (
                          <option key={b.id} value={b.id}>{b.label}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </SimpleGrid>

                <Field.Root>
                  <Field.Label fontSize="sm">Description</Field.Label>
                  <Textarea size="sm" rows={2} {...register('description')} />
                </Field.Root>

                {/* Address */}
                <Field.Root>
                  <Field.Label fontSize="sm">Street Address</Field.Label>
                  <Input size="sm" {...register('streetAddress')} />
                </Field.Root>

                <SimpleGrid columns={3} gap={3} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm">City</Field.Label>
                    <Input size="sm" {...register('city')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">State</Field.Label>
                    <Input size="sm" maxLength={2} {...register('state')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Zip</Field.Label>
                    <Input size="sm" {...register('zip')} />
                  </Field.Root>
                </SimpleGrid>

                <SimpleGrid columns={2} gap={3} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm">County</Field.Label>
                    <Input size="sm" {...register('county')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Address Type</Field.Label>
                    <Input size="sm" placeholder="e.g., Commercial, Industrial" {...register('addressType')} />
                  </Field.Root>
                </SimpleGrid>

                {/* Contact info */}
                <SimpleGrid columns={2} gap={3} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm">Phone</Field.Label>
                    <Input size="sm" {...register('phone')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Email</Field.Label>
                    <Input size="sm" type="email" {...register('email')} />
                  </Field.Root>
                </SimpleGrid>

                {/* Point of contact */}
                <SimpleGrid columns={3} gap={3} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm">POC Name</Field.Label>
                    <Input size="sm" {...register('poc')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">POC Phone</Field.Label>
                    <Input size="sm" {...register('pocPhone')} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">POC Email</Field.Label>
                    <Input size="sm" type="email" {...register('pocEmail')} />
                  </Field.Root>
                </SimpleGrid>

                {/* Coordinates */}
                <SimpleGrid columns={2} gap={3} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm">Latitude</Field.Label>
                    <Input size="sm" type="number" step="any" {...register('latitude', { valueAsNumber: true })} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Longitude</Field.Label>
                    <Input size="sm" type="number" step="any" {...register('longitude', { valueAsNumber: true })} />
                  </Field.Root>
                </SimpleGrid>
                {/* Union coverage (only when creating under a business with unions) */}
                {showUnionCoverage && (
                  <>
                    <Separator />
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>Union Coverage</Text>
                      <Text fontSize="xs" color="gray.500" mb={3}>
                        This business has union affiliations. Set coverage for this worksite:
                      </Text>
                      <VStack gap={2} align="stretch">
                        {businessDetail!.unionAffiliations.map(ua => (
                          <HStack key={ua.unionId} justify="space-between" px={3} py={2} bg="gray.50" borderRadius="md">
                            <HStack gap={2}>
                              <Text fontSize="sm" fontWeight="medium">{ua.unionName}</Text>
                              {ua.localNumber && <Badge fontSize="xs" colorPalette="purple">Local {ua.localNumber}</Badge>}
                            </HStack>
                            <HStack gap={2}>
                              <Text fontSize="xs" color={excludedUnions[ua.unionId] ? 'red.500' : 'green.600'} fontWeight="medium">
                                {excludedUnions[ua.unionId] ? 'Excluded' : 'Covered'}
                              </Text>
                              <Switch.Root
                                size="sm"
                                checked={!excludedUnions[ua.unionId]}
                                onCheckedChange={() => setExcludedUnions(prev => ({
                                  ...prev,
                                  [ua.unionId]: !prev[ua.unionId],
                                }))}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control>
                                  <Switch.Thumb />
                                </Switch.Control>
                              </Switch.Root>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                colorPalette="brand"
                loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
              >
                {isEdit ? 'Save Changes' : 'Create Worksite'}
              </Button>
            </Dialog.Footer>
          </form>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
